import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import type { User } from "oidc-client-ts";
import { callerInfo, completeLogin, getActiveUser, initAuth, login, recoverSession, signup } from "./auth.js";
import { loadConfig } from "./config.js";
import { Layout } from "./components/Layout.js";
import { CenteredCard, RestaurantPicker } from "./pages/RestaurantPicker.js";
import { OnboardingPage } from "./pages/OnboardingPage.js";
import { OrdersPage } from "./pages/OrdersPage.js";
import { InfoPage } from "./pages/InfoPage.js";
import { CallsPage } from "./pages/CallsPage.js";
import { ReservationsPage } from "./pages/ReservationsPage.js";
import { MenuPage } from "./pages/MenuPage.js";
import { HoursPage } from "./pages/HoursPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { shouldResumeSignIn } from "./auth-recovery.js";
import { ErrorPage, errorDetails, type ErrorDetails } from "./error-page.js";
import { PlusIcon, SignInIcon } from "./icons.js";


/**
 * The landing page for Cognito's redirect.
 *
 * Deliberately does NOTHING. Completing the exchange here is what caused sign-in to appear broken: App had
 * already asked getActiveUser() and been told there was no session — because the code had not been
 * exchanged yet — so it rendered the unauthenticated tree. This component then exchanged the code and
 * navigated, but nothing had told App to look again, so the destination rendered the sign-in card. A refresh
 * fixed it, which is exactly the shape of a state-ordering bug.
 *
 * App now completes the exchange in the same pass that reads the user, so the two cannot disagree.
 */
function AuthCallback({ returnTo }: { returnTo?: string }) {
  /*
   * Leave /auth/callback once the code is exchanged, to where sign-in started or to "/", whose page decides
   * where a signed-in user belongs (onboarding, their restaurant, or the picker).
   *
   * THIS MUST BE INSIDE THE ROUTE TABLE. It used to be an early return above <Routes>. That <Navigate>
   * changed the URL, but nothing above the router re-renders on a URL change, so the app kept rendering the
   * <Navigate> itself, which draws nothing: a blank "/" until refreshed. Here, the URL change re-renders the
   * routes and "/" mounts.
   */
  if (returnTo) return <Navigate to={returnTo} replace />;
  return (
    <CenteredCard>
      <p className="muted">Signing in…</p>
    </CenteredCard>
  );
}

/**
 * No session, and the URL names a restaurant.
 *
 * Redirects through Cognito carrying the path, instead of showing the landing card. sessionStorage is
 * per ORIGIN, so an operator arriving from the admin portal has no token here even though it is the same
 * pool and the same account — and Cognito's own session cookie means the round trip usually costs no
 * password, just a flicker.
 *
 * recoverSession() carries the current path as OIDC `state`, which AuthCallback sanitises and navigates
 * to, so the destination survives. Calling login() here instead would lose it and land everyone on the
 * picker, having clicked a specific restaurant.
 *
 * It declines if it already tried moments ago — a cookie that does not produce a session would otherwise
 * bounce forever between here and Cognito — and then the card is shown, which is the honest outcome.
 */
function SignInOrResume() {
  const [resuming, setResuming] = useState(shouldResumeSignIn(window.location.pathname));

  useEffect(() => {
    if (!resuming) return;
    void (async () => {
      const attempted = await recoverSession();
      if (!attempted) setResuming(false);
    })();
  }, [resuming]);

  if (!resuming) return <SignIn />;
  return (
    <CenteredCard>
      <p className="muted">Signing in…</p>
    </CenteredCard>
  );
}

function SignIn() {
  return (
    <CenteredCard>
      <h1 className="brand signin-brand">
        <span className="brand-mark">glowin</span>
      </h1>
      <p className="muted">Your restaurant’s AI phone agent.</p>
      <button className="primary wide with-icon" onClick={() => void login()}>
        <SignInIcon size={16} />
        <span>Sign in</span>
      </button>
      {/*
        Cognito's registration page, reached directly. screen_hint=signup is ignored by the classic
        Hosted UI this pool runs, so this button used to land on the sign-in form. Still the same PKCE
        request and the same /auth/callback — only the page Cognito opens differs.
      */}
      <button className="wide with-icon" onClick={() => void signup()}>
        <PlusIcon size={16} />
        <span>Create an account</span>
      </button>
    </CenteredCard>
  );
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [configured, setConfigured] = useState<boolean>();
  /**
   * Whether this stage has table bookings at all: its config.json carries a reservation API. Prod's does not
   * until the feature ships there, so nothing about bookings is shown — not the tab, not the Settings section.
   */
  const [bookingsAvailable, setBookingsAvailable] = useState(false);
  /** Set only when this load IS the Hosted UI redirect: where to resume, or why it failed. */
  const [callback, setCallback] = useState<{ returnTo?: string; error?: ErrorDetails }>();

  useEffect(() => {
    void (async () => {
      const cfg = await loadConfig();
      if (!cfg) {
        setConfigured(false);
        return;
      }
      initAuth(cfg);
      setBookingsAvailable(Boolean(cfg.reservationApiUrl));
      /*
       * Exchange the authorization code HERE, before anything reads the session. Doing it in the routed
       * component meant this effect had already concluded there was no user, and nothing told it otherwise.
       */
      if (window.location.pathname === "/auth/callback") {
        try {
          setCallback({ returnTo: await completeLogin() });
        } catch (e: unknown) {
          setCallback({ error: errorDetails(e, { context: "sign-in" }) });
        }
      }
      // getActiveUser, not getUser: an expired session used to render a full shell whose
      // every request then failed with 401.
      setUser(await getActiveUser());
      setConfigured(true);
    })();
  }, []);

  if (configured === undefined) return <CenteredCard>Loading…</CenteredCard>;
  if (!configured) {
    // /config.json missing or incomplete: CI writes it from the profile stack's outputs on deploy.
    return (
      <ErrorPage
        details={errorDetails(
          new Error("config.json is missing or incomplete (apiUrl, cognitoAuthority, cognitoClientId)"),
          { context: "startup" }
        )}
      />
    );
  }

  // Includes Cognito sending the browser back with ?error=, e.g. a cancelled sign-in.
  if (callback?.error) return <ErrorPage details={callback.error} />;
  if (!user) {
    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<SignInOrResume />} />
      </Routes>
    );
  }

  const { email } = callerInfo(user);

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback returnTo={callback?.returnTo ?? "/"} />} />
      <Route path="/" element={<RestaurantPicker />} />
      {/* Signed in but no restaurant yet: fill in the application, or see where it stands. */}
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/r/:restaurantId" element={<Layout email={email} bookingsAvailable={bookingsAvailable} />}>
        <Route index element={<Navigate to="orders" replace />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="menu" element={<MenuPage />} />
        {bookingsAvailable ? <Route path="reservations" element={<ReservationsPage />} /> : null}
        <Route path="calls" element={<CallsPage />} />
        <Route path="hours" element={<HoursPage />} />
        <Route path="info" element={<InfoPage />} />
        <Route path="settings" element={<SettingsPage bookingsAvailable={bookingsAvailable} />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
